/**
 * startPollingRenameJob.ts
 * This file contains the logic to poll the status of a rename job.
 * It checks the job status at regular intervals and updates the context accordingly.
 * If the job is complete, it dispatches a finish action and calls an optional success callback
 * if provided.
 * It also handles errors during polling and clears the interval when the job is done.
 * * The polling interval is set to 3000 milliseconds (3 seconds).
 *
 *
 */
import { getJobStatusByUniqueIdentifier } from '../api/jobs';
import { JobStatusType } from '../../api-client/autogenerated';
import { isRenameJobComplete } from '../../main-components/design/context/utils/renameJobHelpers';
import React from 'react';

type PollRenameJobParams = {
  jobId: string;
  folderKey: string;
  dispatch: React.Dispatch<{
    type: 'START_RENAME' | 'UPDATE_RENAME_PROGRESS' | 'FINISH_RENAME' | 'CLEAR_RENAME';
    payload: any;
  }>;
  onSuccess?: () => void;
};

let lastPercent = -1;
let lastStatus: JobStatusType | null = null;

export function startPollingRenameJob({
  jobId,
  folderKey,
  dispatch,
  onSuccess,
}: PollRenameJobParams) {
  const interval = setInterval(async () => {
    try {
      const status = await getJobStatusByUniqueIdentifier(jobId);

      if (!status || !status.progress) return;

      const percent = status.progress?.percent ?? 0;
      const jobStatus = status.progress?.status ?? status.status;

      // Only update if the percent or status has changed
      if (percent !== lastPercent || jobStatus !== lastStatus) {
        lastPercent = percent;
        lastStatus = jobStatus;

        // Use requestAnimationFrame to ensure UI updates don't interfere with dialog state
        window.requestAnimationFrame(() => {
          dispatch({
            type: 'UPDATE_RENAME_PROGRESS',
            payload: { folderKey, percent },
          });
        });
      }

      const isDone = isRenameJobComplete({ status: jobStatus, percent });

      if (isDone) {
        dispatch({
          type: 'FINISH_RENAME',
          payload: {
            folderKey,
            status:
              jobStatus === JobStatusType.Initiated && percent === 100
                ? JobStatusType.Complete
                : jobStatus,
          },
        });

        if (jobStatus === JobStatusType.Complete && typeof onSuccess === 'function') {
          onSuccess();
        }
        // clear the entry using the same key we started with
        window.requestAnimationFrame(() => {
          dispatch({ type: 'CLEAR_RENAME', payload: { folderKey, jobId } });
        });
        clearInterval(interval);
      }
    } catch (error) {
      console.error('[Renaming] Polling failed', error);
      clearInterval(interval);
    }
  }, 3000);
}
